/**
 * MAISON AURUM — JS Premium
 * Animations · Interactions · Navbar · Cursor
 * Version 2.0 — Production Ready
 * Aucune dépendance externe requise
 */

(function () {
  'use strict';

  /* ─── UTILITAIRES ──────────────────────────────────────── */
  const qs  = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const on  = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);

  const isMobile = () => window.innerWidth < 768;
  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ─── LOADER PAGE ──────────────────────────────────────── */
  function initLoader() {
    const loader = qs('.ma-loader');
    if (!loader) return;

    window.addEventListener('load', () => {
      setTimeout(() => {
        loader.classList.add('is-hidden');
        // Supprimer du DOM après la transition
        setTimeout(() => loader.remove(), 500);
      }, 600);
    });
  }

  /* ─── NAVBAR SCROLL ────────────────────────────────────── */
  function initNavbar() {
    const header = qs(
      '.shopify-section-header, header.site-header, #shopify-section-header, .site-header'
    );
    if (!header) return;

    let lastScroll = 0;
    let ticking    = false;

    function onScroll() {
      const current = window.scrollY;

      if (!ticking) {
        requestAnimationFrame(() => {
          // Ajoute la classe scrolled
          header.classList.toggle('is-scrolled', current > 60);

          // Cache la navbar en descendant rapidement (mobile)
          if (isMobile()) {
            const goingDown = current > lastScroll && current > 120;
            header.classList.toggle('is-hidden-scroll', goingDown);
          }

          lastScroll = current;
          ticking    = false;
        });
        ticking = true;
      }
    }

    on(window, 'scroll', onScroll, { passive: true });

    // Ajouter la classe CSS pour is-hidden-scroll dans la navbar
    // .site-header.is-hidden-scroll { transform: translateY(-100%); }
  }

  /* ─── MENU MOBILE FULLSCREEN ───────────────────────────── */
  function initMobileMenu() {
    const triggers = qsa('[data-menu-toggle], .site-header__menu-toggle, .header__icon--menu');
    const drawer   = qs('.mobile-nav, .menu-drawer, [data-mobile-menu]');
    const close    = qsa('[data-menu-close], .mobile-nav__close, .menu-drawer__close-button');
    const body     = document.body;

    if (!drawer) return;

    let isOpen = false;

    function openMenu() {
      isOpen = true;
      drawer.classList.add('is-open');
      body.style.overflow = 'hidden';

      // Animation cascade des liens
      const links = qsa('a', drawer);
      links.forEach((link, i) => {
        link.style.transitionDelay = `${i * 60 + 120}ms`;
        link.style.opacity = '0';
        link.style.transform = 'translateY(16px)';
        requestAnimationFrame(() => {
          link.style.opacity = '1';
          link.style.transform = 'translateY(0)';
        });
      });
    }

    function closeMenu() {
      isOpen = false;
      drawer.classList.remove('is-open');
      body.style.overflow = '';
    }

    triggers.forEach(btn => on(btn, 'click', () => isOpen ? closeMenu() : openMenu()));
    close.forEach(btn => on(btn, 'click', closeMenu));

    // ESC pour fermer
    on(document, 'keydown', e => e.key === 'Escape' && isOpen && closeMenu());
  }

  /* ─── SCROLL REVEAL ────────────────────────────────────── */
  function initReveal() {
    if (prefersReducedMotion()) {
      // Révèle tout immédiatement sans animation
      qsa('[data-reveal], .reveal').forEach(el => el.classList.add('is-visible'));
      return;
    }

    const elements = qsa('[data-reveal], .reveal');
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold:  0.12,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    elements.forEach(el => observer.observe(el));
  }

  /* ─── REVEAL AUTO — ÉLÉMENTS SANS ATTRIBUT ─────────────── */
  function initAutoReveal() {
    if (prefersReducedMotion()) return;

    // Cibles automatiques (si non déjà annotées)
    const autoTargets = [
      '.product-card',
      '.editorial-block__content > *',
      '.section__header > *',
      '.testimonial__quote',
      '.product__title',
    ].join(', ');

    qsa(autoTargets).forEach((el, i) => {
      if (!el.hasAttribute('data-reveal') && !el.classList.contains('reveal')) {
        el.setAttribute('data-reveal', 'slide-up');
        // Décalage automatique pour les enfants du même parent
        const siblings = qsa('[data-reveal]', el.parentElement);
        const delay    = Math.min(siblings.indexOf(el), 4);
        if (delay > 0) el.setAttribute('data-reveal-delay', delay);
      }
    });

    // Re-observer les nouveaux éléments
    initReveal();
  }

  /* ─── PARALLAXE HERO ───────────────────────────────────── */
  function initParallax() {
    if (prefersReducedMotion() || isMobile()) return;

    const heroMedia = qsa('.hero__media--parallax img, .hero__media--parallax video');
    if (!heroMedia.length) return;

    let ticking = false;

    function applyParallax() {
      const scrollY = window.scrollY;
      heroMedia.forEach(el => {
        const parent = el.closest('.hero, .hero-section');
        if (!parent) return;
        const rect   = parent.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        const speed  = 0.3;
        el.style.transform = `translateY(${scrollY * speed}px) scale(1.12)`;
      });
      ticking = false;
    }

    on(window, 'scroll', () => {
      if (!ticking) {
        requestAnimationFrame(applyParallax);
        ticking = true;
      }
    }, { passive: true });
  }

  /* ─── CURSEUR CUSTOM ───────────────────────────────────── */
  function initCursor() {
    if (isMobile() || window.matchMedia('(pointer: coarse)').matches) return;

    // Créer le curseur
    const cursor = document.createElement('div');
    cursor.className = 'ma-cursor';
    document.body.appendChild(cursor);

    let mouseX = 0, mouseY = 0;
    let curX   = 0, curY   = 0;

    // Suivi fluide (lerp)
    function lerp(a, b, n) { return (1 - n) * a + n * b; }

    function loop() {
      curX = lerp(curX, mouseX, 0.14);
      curY = lerp(curY, mouseY, 0.14);
      cursor.style.transform = `translate3d(${curX - 5}px, ${curY - 5}px, 0)`;
      requestAnimationFrame(loop);
    }

    on(document, 'mousemove', e => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    });

    // Agrandir sur hover interactif
    const hoverTargets = 'a, button, .btn, .product-card, [class*="swatch"]';
    on(document, 'mouseover', e => {
      if (e.target.closest(hoverTargets)) cursor.classList.add('ma-cursor--hover');
    });
    on(document, 'mouseout', e => {
      if (e.target.closest(hoverTargets)) cursor.classList.remove('ma-cursor--hover');
    });

    loop();
  }

  /* ─── MARQUEE PAUSE ON HOVER ───────────────────────────── */
  function initMarquee() {
    qsa('.marquee__track').forEach(track => {
      // Dupliquer pour boucle infinie
      if (!track.dataset.cloned) {
        const clone = track.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        track.parentElement.appendChild(clone);
        track.dataset.cloned = 'true';
      }
    });
  }

  /* ─── TRANSITION DE PAGE ───────────────────────────────── */
  function initPageTransitions() {
    if (prefersReducedMotion()) return;

    // Créer le voile de transition
    const curtain = document.createElement('div');
    curtain.className = 'ma-transition';
    document.body.appendChild(curtain);

    // Animer l'entrée sur chaque page
    requestAnimationFrame(() => {
      curtain.classList.add('exit');
      setTimeout(() => curtain.classList.remove('exit'), 500);
    });

    // Capter les clicks de navigation interne
    on(document, 'click', e => {
      const link = e.target.closest('a');
      if (!link) return;

      const href = link.getAttribute('href');
      if (
        !href ||
        href.startsWith('#') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        link.target === '_blank' ||
        e.metaKey || e.ctrlKey || e.shiftKey
      ) return;

      // Lien interne Shopify
      const isInternal =
        href.startsWith('/') ||
        href.includes(window.location.hostname);

      if (!isInternal) return;

      e.preventDefault();
      curtain.classList.remove('exit');
      curtain.classList.add('enter');

      setTimeout(() => {
        window.location.href = href;
      }, 460);
    });
  }

  /* ─── STICKY ADD-TO-CART (mobile) ─────────────────────── */
  function initStickyCart() {
    if (!isMobile()) return;

    const productForm = qs('.product-form, [action*="/cart/add"]');
    const addToCart   = productForm && qs('[type="submit"], .btn--add-to-cart', productForm);
    if (!addToCart) return;

    const stickyBar = document.createElement('div');
    stickyBar.className = 'ma-sticky-cart';
    stickyBar.innerHTML = `
      <button class="btn btn--primary ma-sticky-cart__btn">${addToCart.textContent}</button>
    `;
    stickyBar.style.cssText = `
      position: fixed;
      bottom: 0; left: 0; right: 0;
      padding: 1rem;
      background: rgba(253,250,246,0.95);
      backdrop-filter: blur(16px);
      border-top: 1px solid var(--ma-cream-deep);
      z-index: 800;
      transform: translateY(100%);
      transition: transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
    `;
    document.body.appendChild(stickyBar);

    const stickyBtn = qs('.ma-sticky-cart__btn', stickyBar);

    // Afficher lorsque le vrai bouton sort de l'écran
    const io = new IntersectionObserver(([entry]) => {
      const visible = !entry.isIntersecting;
      stickyBar.style.transform = visible ? 'translateY(0)' : 'translateY(100%)';
    }, { threshold: 0 });

    io.observe(addToCart);

    // Soumettre le formulaire au clic
    on(stickyBtn, 'click', () => addToCart.click());
  }

  /* ─── SMOOTH SCROLL INTERNE ────────────────────────────── */
  function initSmoothScroll() {
    qsa('a[href^="#"]').forEach(anchor => {
      on(anchor, 'click', e => {
        const target = qs(anchor.getAttribute('href'));
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* ─── HOVER IMAGE TILT (desktop) ──────────────────────── */
  function initImageTilt() {
    if (isMobile() || prefersReducedMotion()) return;

    qsa('.editorial-block__media').forEach(el => {
      on(el, 'mousemove', e => {
        const rect   = el.getBoundingClientRect();
        const cx     = rect.left + rect.width / 2;
        const cy     = rect.top  + rect.height / 2;
        const dx     = (e.clientX - cx) / rect.width;
        const dy     = (e.clientY - cy) / rect.height;
        const img    = qs('img, video', el);
        if (img) {
          img.style.transform = `scale(1.04) translate(${dx * 6}px, ${dy * 4}px)`;
        }
      });

      on(el, 'mouseleave', () => {
        const img = qs('img, video', el);
        if (img) {
          img.style.transform = '';
          img.style.transition = 'transform 0.9s cubic-bezier(0.25, 0.1, 0.25, 1)';
        }
      });
    });
  }

  /* ─── COMPTEUR STOCK / URGENCE ─────────────────────────── */
  function initStockAlert() {
    qsa('[data-stock-count]').forEach(el => {
      const count = parseInt(el.dataset.stockCount, 10);
      if (count > 0 && count <= 5) {
        el.textContent = `Plus que ${count} en stock`;
        el.style.color = 'var(--ma-teal-dark)';
        el.style.fontSize = 'var(--text-micro)';
        el.style.letterSpacing = '0.08em';
      }
    });
  }

  /* ─── LAZY LOADING IMAGES ──────────────────────────────── */
  function initLazyLoad() {
    if ('loading' in HTMLImageElement.prototype) {
      // Support natif — ajouter loading="lazy" via Liquid
      qsa('img:not([loading])').forEach(img => {
        img.setAttribute('loading', 'lazy');
      });
    }

    // Fallback Intersection Observer
    const lazyImages = qsa('img[data-src], img.lazyload');
    if (!lazyImages.length) return;

    const io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const img = entry.target;
        if (img.dataset.src) img.src = img.dataset.src;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.classList.remove('lazyload');
        img.classList.add('lazyloaded');
        io.unobserve(img);
      });
    }, { rootMargin: '200px' });

    lazyImages.forEach(img => io.observe(img));
  }

  /* ─── INIT GLOBAL ──────────────────────────────────────── */
  function init() {
    initLoader();
    initNavbar();
    initMobileMenu();
    initReveal();
    initAutoReveal();
    initParallax();
    initCursor();
    initMarquee();
    initPageTransitions();
    initStickyCart();
    initSmoothScroll();
    initImageTilt();
    initStockAlert();
    initLazyLoad();
  }

  // Lancer après le DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ── Exposer pour usage externe si besoin ──
  window.MaisonAurum = {
    initReveal,
    initNavbar,
    initMobileMenu,
  };

})();